import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { Section } from '../ui/Section';
import { Card } from '../ui/Card';
import { GeometricPattern } from '../../utils/patterns';
import { typography } from '../../utils/theme';
import { CheckCircleIcon } from 'lucide-react';
// Custom SVG Icons for expertise domains
const CustomPaletteIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" className={className} data-id="element-188">
    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C13.66 22 15 20.66 15 19C15 18.3 14.74 17.66 14.31 17.17C13.9 16.71 13.66 16.12 13.66 15.5C13.66 14.12 14.78 13 16.16 13H18C20.21 13 22 11.21 22 9C22 5.13 17.52 2 12 2Z" stroke="currentColor" strokeWidth="1.5" data-id="element-189" />
    <circle cx="7" cy="10" r="1.5" fill="currentColor" data-id="element-190" />
    <circle cx="12" cy="7" r="1.5" fill="currentColor" data-id="element-191" />
    <circle cx="17" cy="10" r="1.5" fill="currentColor" data-id="element-192" />
    <circle cx="9" cy="15" r="1.5" fill="currentColor" data-id="element-193" />
  </svg>;
const CustomBrainIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" className={className} data-id="element-194">
    <path d="M9.5 2C7.01 2 5 4.01 5 6.5C5 7.14 5.13 7.75 5.36 8.3C3.87 9.08 3 10.62 3 12.5C3 14.38 3.87 15.92 5.36 16.7C5.13 17.25 5 17.86 5 18.5C5 20.99 7.01 23 9.5 23C10.45 23 11.33 22.71 12.07 22.22C12.38 22.4 12.69 22.57 13 22.72C13.31 22.57 13.62 22.4 13.93 22.22C14.67 22.71 15.55 23 16.5 23C18.99 23 21 20.99 21 18.5C21 17.86 20.87 17.25 20.64 16.7C22.13 15.92 23 14.38 23 12.5C23 10.62 22.13 9.08 20.64 8.3C20.87 7.75 21 7.14 21 6.5C21 4.01 18.99 2 16.5 2C15.55 2 14.67 2.29 13.93 2.78C13.62 2.6 13.31 2.43 13 2.28C12.69 2.43 12.38 2.6 12.07 2.78C11.33 2.29 10.45 2 9.5 2Z" stroke="currentColor" strokeWidth="1.5" data-id="element-195" />
    <circle cx="10" cy="10" r="1" fill="currentColor" data-id="element-196" />
    <circle cx="14" cy="10" r="1" fill="currentColor" data-id="element-197" />
    <circle cx="12" cy="14" r="1" fill="currentColor" data-id="element-198" />
    <path d="M9 12L12 14L15 12" stroke="currentColor" strokeWidth="1" strokeLinecap="round" data-id="element-199" />
  </svg>;
const CustomCodeBracesIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" className={className} data-id="element-200">
    <path d="M8 3C6.34 3 5 4.34 5 6V9C5 10.1 4.1 11 3 11V13C4.1 13 5 13.9 5 15V18C5 19.66 6.34 21 8 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-201" />
    <path d="M16 3C17.66 3 19 4.34 19 6V9C19 10.1 19.9 11 21 11V13C19.9 13 19 13.9 19 15V18C19 19.66 17.66 21 16 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-202" />
    <circle cx="12" cy="8" r="1" fill="currentColor" data-id="element-203" />
    <circle cx="12" cy="12" r="1" fill="currentColor" data-id="element-204" />
    <circle cx="12" cy="16" r="1" fill="currentColor" data-id="element-205" />
  </svg>;
const CustomLayersStackIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" className={className} data-id="element-206">
    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" data-id="element-207" />
    <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" data-id="element-208" />
    <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" data-id="element-209" />
    <circle cx="12" cy="7" r="1.5" fill="currentColor" data-id="element-210" />
    <circle cx="12" cy="12" r="1" fill="currentColor" data-id="element-211" />
    <circle cx="12" cy="17" r="1" fill="currentColor" data-id="element-212" />
  </svg>;
export function ExpertiseApp() {
  const {
    getThemeClasses,
    t
  } = useTheme();
  const themeClasses = getThemeClasses();
  const [selectedDomain, setSelectedDomain] = useState<string>('ux-design');
  const [hoveredSkill, setHoveredSkill] = useState<string | null>(null);
  const domains = [{
    id: 'ux-design',
    icon: CustomPaletteIcon,
    color: 'from-purple-500 to-violet-500',
    pattern: 'circles',
    title: 'Design & UX',
    focus: 'Intuïtieve en emotionele digitale ervaringen creëren',
    approach: 'UX-concepting en flows, visual direction, storytelling door interface',
    skills: ['UI Design', 'User Research', 'Figma', 'Accessibility'],
    rotation: -2
  }, {
    id: 'ai-integration',
    icon: CustomBrainIcon,
    color: 'from-blue-500 to-cyan-500',
    pattern: 'diamond',
    title: 'AI & Creatie',
    focus: 'AI combineren met creatieve storytelling en visuele identiteit',
    approach: 'Character building, AI-video en animatie, AI-driven prototypes',
    skills: ['AI/ML Integration', 'Prompt Engineering', 'Data Visualization', 'Python'],
    rotation: 3
  }, {
    id: 'development',
    icon: CustomCodeBracesIcon,
    color: 'from-emerald-500 to-teal-500',
    pattern: 'square-rotate',
    title: 'Technologie & Development',
    focus: 'Technische systemen begrijpen en implementeren',
    approach: 'React componenten, front-end gedrag, API-data interpretatie',
    skills: ['React', 'TypeScript', 'Tailwind CSS', 'Performance Optimization'],
    rotation: -1
  }, {
    id: 'systems-thinking',
    icon: CustomLayersStackIcon,
    color: 'from-orange-500 to-amber-500',
    pattern: 'triangles',
    title: 'Strategie & Concept',
    focus: 'Van idee tot functioneel prototype',
    approach: 'Procesanalyse, creativiteit met technische haalbaarheid',
    skills: ['Design Systems', 'Workflow Optimization', 'Systems Architecture'],
    rotation: 2
  }];
  const currentDomain = domains.find(d => d.id === selectedDomain) || domains[0];
  const DomainIcon = currentDomain.icon;
  return <div className={`p-4 md:p-6 lg:p-8 ${themeClasses.text} max-w-7xl mx-auto`} data-id="element-213">
      {/* Header with pinboard style */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-8" data-id="element-214">
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-3" data-id="element-215">
          {t.expertise?.title || 'Expertise & Tools'}
        </h1>
        <p className={`text-base md:text-lg ${themeClasses.text} opacity-70 max-w-3xl`} data-id="element-216">
          {t.expertise?.description || 'Ik werk op het snijvlak van design, AI en gebruikerservaring.'}
        </p>
      </motion.div>
      {/* Domain selector - pinboard cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-217">
        {domains.map((domain, index) => {
        const Icon = domain.icon;
        const isSelected = selectedDomain === domain.id;
        return <motion.button key={domain.id} initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1,
          rotate: isSelected ? 0 : domain.rotation
        }} transition={{
          delay: index * 0.1
        }} whileHover={{
          scale: 1.05,
          rotate: 0
        }} onClick={() => setSelectedDomain(domain.id)} className={`relative p-5 rounded-2xl transition-all ${isSelected ? `bg-gradient-to-br ${domain.color} text-white shadow-lg` : `${themeClasses.card} border-2 ${themeClasses.border}`}`} style={{
          boxShadow: isSelected ? '0 15px 30px -8px rgba(139, 92, 246, 0.4)' : '0 8px 20px -8px rgba(0, 0, 0, 0.2)'
        }} data-id="element-218">
              {!isSelected && <div className={`absolute inset-0 bg-gradient-to-br ${domain.color} opacity-0 hover:opacity-10 transition-opacity rounded-2xl`} data-id="element-219" />}
              <div className="relative" data-id="element-220">
                {isSelected && <div className="absolute inset-0 opacity-20" data-id="element-221">
                    <GeometricPattern pattern={domain.pattern} size="small" data-id="element-222" />
                  </div>}
                <Icon className={`w-8 h-8 mx-auto mb-3 relative z-10 ${isSelected ? 'text-white' : themeClasses.icon}`} data-id="element-223" />
              </div>
              <p className={`text-sm font-semibold ${isSelected ? 'text-white' : themeClasses.text}`} data-id="element-224">
                {domain.title}
              </p>
              {/* Pin effect for selected */}
              {isSelected && <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg z-10" data-id="element-225" />}
            </motion.button>;
      })}
      </div>
      {/* Domain detail - styled card */}
      <motion.div key={selectedDomain} initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} className="relative" data-id="element-226">
        <div className={`relative rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl`} style={{
        boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)',
        transform: 'rotate(-0.3deg)'
      }} data-id="element-227">
          {/* Gradient header */}
          <div className={`relative p-6 md:p-8 bg-gradient-to-br ${currentDomain.color} text-white`} data-id="element-228">
            <div className="absolute inset-0 opacity-20" data-id="element-229">
              <GeometricPattern pattern={currentDomain.pattern} size="large" data-id="element-230" />
            </div>
            <div className="relative z-10 flex items-center gap-4 mb-4" data-id="element-231">
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center relative overflow-hidden" data-id="element-232">
                <GeometricPattern pattern={currentDomain.pattern} size="medium" className="opacity-30" data-id="element-233" />
                <DomainIcon className="w-8 h-8 text-white relative z-10" data-id="element-234" />
              </div>
              <div data-id="element-235">
                <h2 className="text-2xl md:text-3xl font-bold" data-id="element-236">
                  {currentDomain.title}
                </h2>
              </div>
            </div>
          </div>
          {/* Content */}
          <div className="p-6 md:p-8" data-id="element-237">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-238">
              <div data-id="element-239">
                <h3 className={`${typography.h4} mb-3`} data-id="element-240">Focus</h3>
                <p className={`${typography.body} opacity-80 mb-6`} data-id="element-241">
                  {currentDomain.focus}
                </p>
                <h3 className={`${typography.h4} mb-3`} data-id="element-242">Mijn Aanpak</h3>
                <p className={`${typography.body} opacity-80`} data-id="element-243">
                  {currentDomain.approach}
                </p>
              </div>
              <div data-id="element-244">
                <h3 className={`${typography.h4} mb-4`} data-id="element-245">Vaardigheden</h3>
                <div className="grid grid-cols-2 gap-3" data-id="element-246">
                  {currentDomain.skills.map((skill, index) => <motion.div key={skill} initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.05
                }} onHoverStart={() => setHoveredSkill(skill)} onHoverEnd={() => setHoveredSkill(null)} className={`flex items-center gap-2 p-3 rounded-lg ${themeClasses.card} border ${themeClasses.border} cursor-pointer transition-all ${hoveredSkill === skill ? 'scale-105' : ''}`} data-id="element-247">
                      <motion.div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${currentDomain.color}`} animate={hoveredSkill === skill ? {
                    scale: [1, 1.5, 1],
                    opacity: [1, 0.5, 1]
                  } : {}} transition={{
                    duration: 0.5,
                    repeat: hoveredSkill === skill ? Infinity : 0
                  }} data-id="element-248" />
                      <span className="text-sm" data-id="element-249">{skill}</span>
                    </motion.div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Pin effect */}
        <div className="absolute -top-3 right-8 w-6 h-6 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg z-10" data-id="element-250" />
      </motion.div>
    </div>;
}